import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, RefreshCw, Loader2, CheckCircle2, XCircle, HelpCircle,
  AlertTriangle, Shield, ClipboardCheck, BarChart3,
  ThumbsUp, ThumbsDown, Flag, Phone, Mail, Truck, Hash,
  Building2, MapPin, Clock, ShieldCheck, ShieldAlert, ShieldX,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Shared helpers ─────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function fmtDateShort(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}
function relTime(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs  < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function scoreColor(s) {
  return s >= 80 ? 'text-emerald-500' : s >= 55 ? 'text-amber-500' : 'text-red-500';
}
function scoreBorderColor(s) {
  return s >= 80 ? 'border-emerald-500' : s >= 55 ? 'border-amber-500' : 'border-red-500';
}
function riskColor(r) {
  if (!r) return 'text-muted-foreground';
  return r === 'low' ? 'text-emerald-500' : r === 'medium' ? 'text-amber-500' : 'text-red-500';
}

function PassIcon({ pass, size = 'h-4 w-4' }) {
  if (pass === true)  return <CheckCircle2 className={`${size} text-emerald-500 shrink-0`} />;
  if (pass === false) return <XCircle      className={`${size} text-red-500 shrink-0`} />;
  return                     <HelpCircle  className={`${size} text-muted-foreground shrink-0`} />;
}

function StatusBadge({ status }) {
  const cfg = {
    ACTIVE:          { cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', label: 'Active' },
    AUTHORIZED:      { cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', label: 'Authorized' },
    INACTIVE:        { cls: 'bg-red-500/10 text-red-500', label: 'Inactive' },
    NOT_REGISTERED:  { cls: 'bg-red-500/10 text-red-500', label: 'Not Registered' },
    deliverable:     { cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', label: 'Deliverable' },
    undeliverable:   { cls: 'bg-red-500/10 text-red-500', label: 'Undeliverable' },
    risky:           { cls: 'bg-amber-500/10 text-amber-500', label: 'Risky' },
    mobile:          { cls: 'bg-blue-500/10 text-blue-500', label: 'Mobile' },
    landline:        { cls: 'bg-slate-400/10 text-muted-foreground', label: 'Landline' },
    voip:            { cls: 'bg-amber-500/10 text-amber-500', label: 'VoIP' },
    low:             { cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', label: 'Low Risk' },
    medium:          { cls: 'bg-amber-500/10 text-amber-500', label: 'Medium Risk' },
    high:            { cls: 'bg-red-500/10 text-red-500', label: 'High Risk' },
  };
  const c = cfg[status] || { cls: 'bg-muted text-muted-foreground', label: status || '—' };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${c.cls}`}>{c.label}</span>;
}

function DetailRow({ label, value, valueClass = '' }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border last:border-0">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className={`text-xs font-medium text-right ${valueClass || 'text-foreground'}`}>{value}</span>
    </div>
  );
}

function SectionCard({ icon: Icon, title, badge, timestamp, children }) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="font-semibold text-sm text-foreground flex-1">{title}</span>
        {badge}
        {timestamp && (
          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />{relTime(timestamp)}
          </span>
        )}
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  );
}

function Flag2({ text, color = 'amber' }) {
  const cls = color === 'red'
    ? 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'
    : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20';
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${cls} mt-2`}>
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      <span className="text-xs font-medium">{text}</span>
    </div>
  );
}


// ── Page ───────────────────────────────────────────────────────────────────────

export default function CarrierVettingPage() {
  const { carrierId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [report, setReport]           = useState(null);
  const [carrier, setCarrier]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [refreshingFmcsa, setRefreshingFmcsa] = useState(false);
  const [refreshingContacts, setRefreshingContacts] = useState(false);
  const [decision, setDecision]       = useState('');
  const [notes, setNotes]             = useState('');
  const [submitting, setSubmitting]   = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchCarrier = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}`, { method: 'POST', headers: authHeaders });
      if (res.ok) setCarrier(await res.json());
    } catch { /* silent */ }
  }, [carrierId, token]); // eslint-disable-line

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/vetting-report`, { method: 'POST', headers: authHeaders });
      if (!res.ok) throw new Error('Failed to load vetting report');
      const data = await res.json();
      setReport(data);
      setDecision(data.vetting_status || '');
      setNotes(data.vetting_notes || '');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [carrierId, token]); // eslint-disable-line

  useEffect(() => { fetchCarrier(); fetchReport(); }, [fetchCarrier, fetchReport]);

  const handleRefreshFmcsa = async () => {
    setRefreshingFmcsa(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/fmcsa-check`, {
        method: 'POST', headers: authHeaders,
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'FMCSA check failed');
      toast.success('FMCSA data refreshed');
      await fetchReport();
    } catch (err) { toast.error(err.message); }
    finally { setRefreshingFmcsa(false); }
  };

  const handleRefreshContacts = async () => {
    setRefreshingContacts(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/validate-contacts`, {
        method: 'POST', headers: authHeaders,
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Validation failed');
      toast.success('Contact details refreshed');
      await fetchReport();
    } catch (err) { toast.error(err.message); }
    finally { setRefreshingContacts(false); }
  };

  const handleDecision = async () => {
    if (!decision) { toast.error('Select a decision first'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/vetting-decision`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, notes }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to save');
      toast.success(`Carrier ${decision}`);
      await fetchReport();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-32 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading vetting report…
    </div>
  );

  const carrierName = carrier?.company_name || 'Carrier';
  const score       = report?.score ?? 0;
  const rec         = report?.recommendation;
  const recCfg = rec === 'approve'
    ? { label: 'Approve',       cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', Icon: ShieldCheck }
    : rec === 'review'
    ? { label: 'Manual Review', cls: 'bg-amber-500/10  text-amber-600  dark:text-amber-400',  Icon: ShieldAlert }
    : { label: 'Reject',        cls: 'bg-red-500/10    text-red-600    dark:text-red-400',    Icon: ShieldX    };

  const vsCfg = {
    approved: { label: 'Approved',         cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
    flagged:  { label: 'Flagged',          cls: 'bg-amber-500/10  text-amber-600  dark:text-amber-400'  },
    rejected: { label: 'Rejected',         cls: 'bg-red-500/10    text-red-600    dark:text-red-400'    },
  }[report?.vetting_status] || { label: 'Not yet reviewed', cls: 'bg-muted text-muted-foreground' };

  const fmcsa  = report;   // vetting report contains all FMCSA-derived fields at top level
  const phone  = report?.phone_validation  || {};
  const email  = report?.email_validation  || {};
  const checklist    = report?.checklist || [];
  const basics       = report?.basics    || [];
  const blockers     = checklist.filter(i => i.blocker);
  const nonBlockers  = checklist.filter(i => !i.blocker);

  // Pull latest FMCSA detail from checklist values
  const authorityStatus    = checklist.find(i => i.key === 'authority_active')?.value;
  const allowedToOperate   = checklist.find(i => i.key === 'allowed_to_operate')?.value;
  const safetyRating       = checklist.find(i => i.key === 'safety_rating_ok')?.value;
  const authorityAge       = checklist.find(i => i.key === 'authority_age')?.value;
  const insuranceStatus    = checklist.find(i => i.key === 'insurance_on_file')?.value;
  const oosStatus          = checklist.find(i => i.key === 'not_oos')?.value;
  const insMin             = checklist.find(i => i.key === 'insurance_min');
  const fleetItem          = checklist.find(i => i.key === 'has_active_fleet');

  return (
    <div className="space-y-4 max-w-4xl">

      {/* ── Header ── */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-start gap-3 flex-wrap">
          <button onClick={() => navigate(`/admin/carriers/${carrierId}`)}
            className="text-muted-foreground hover:text-foreground transition-colors mt-0.5 shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Shield className="h-4 w-4 text-muted-foreground shrink-0" />
              <h1 className="font-outfit text-lg font-bold text-foreground">
                Carrier Vetting — {carrierName}
              </h1>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${vsCfg.cls}`}>
                {vsCfg.label}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {carrier?.dot_number && <span>DOT# <span className="font-mono text-foreground">{carrier.dot_number}</span></span>}
              {carrier?.mc_number  && <span>MC# <span className="font-mono text-foreground">{carrier.mc_number}</span></span>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <button onClick={handleRefreshFmcsa} disabled={refreshingFmcsa}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${refreshingFmcsa ? 'animate-spin' : ''}`} />
              {refreshingFmcsa ? 'Checking FMCSA…' : 'Refresh FMCSA'}
            </button>
            <button onClick={handleRefreshContacts} disabled={refreshingContacts}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${refreshingContacts ? 'animate-spin' : ''}`} />
              {refreshingContacts ? 'Validating…' : 'Refresh Contacts'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Score + recommendation + decision (3 cols) ── */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-xl p-4 flex flex-col items-center justify-center gap-2">
          <div className={`w-24 h-24 rounded-full border-4 ${scoreBorderColor(score)} flex items-center justify-center`}>
            <span className={`text-3xl font-bold tabular-nums ${scoreColor(score)}`}>{score}</span>
          </div>
          <p className="text-xs text-muted-foreground">Vetting score / {report?.max_score ?? 110}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 flex flex-col justify-center gap-3">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">System Recommendation</p>
          <div className={`flex items-center gap-2 self-start px-3 py-1.5 rounded-full ${recCfg.cls}`}>
            <recCfg.Icon className="h-4 w-4" />
            <span className="text-sm font-semibold">{recCfg.label}</span>
          </div>
          {!report?.has_fmcsa_check && (
            <p className="text-xs text-muted-foreground">Run FMCSA check for a full score</p>
          )}
          {report?.fmcsa_checked_at && (
            <p className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />FMCSA {relTime(report.fmcsa_checked_at)}
            </p>
          )}
        </div>
        <div className="bg-card border border-border rounded-xl p-4 flex flex-col justify-center gap-3">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Admin Decision</p>
          <span className={`self-start px-3 py-1.5 rounded-full text-sm font-semibold ${vsCfg.cls}`}>
            {vsCfg.label}
          </span>
          {report?.vetting_reviewed_by && (
            <p className="text-[10px] text-muted-foreground">
              by {report.vetting_reviewed_by}
              {report.vetting_reviewed_at && <> · {fmtDateShort(report.vetting_reviewed_at)}</>}
            </p>
          )}
        </div>
      </div>

      {/* ── USDOT & Authority ── */}
      <SectionCard
        icon={Hash}
        title="USDOT & Operating Authority"
        badge={
          authorityStatus
            ? <StatusBadge status={authorityStatus} />
            : <span className="text-[10px] text-muted-foreground">No FMCSA check</span>
        }
        timestamp={report?.fmcsa_checked_at}
      >
        {!report?.has_fmcsa_check ? (
          <p className="text-sm text-muted-foreground py-2 text-center">
            No FMCSA check on record — click <strong>Refresh FMCSA</strong> to run one.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-x-8">
            <div>
              <DetailRow label="US DOT Number" value={
                <span className="font-mono">{carrier?.dot_number || '—'}</span>
              } />
              <DetailRow label="MC Number" value={
                carrier?.mc_number
                  ? <span className="font-mono">{carrier.mc_number}</span>
                  : '—'
              } />
              <DetailRow label="FMCSA Legal Name"  value={report?.fmcsa_legal_name} />
              <DetailRow label="FMCSA DBA Name"    value={report?.fmcsa_dba_name} />
              <DetailRow label="Carrier Operation" value={report?.fmcsa_carrier_operation} />
              <DetailRow label="Authority Status"  value={authorityStatus} valueClass={authorityStatus === 'ACTIVE' ? 'text-emerald-500 font-semibold' : 'text-red-500 font-semibold'} />
              <DetailRow label="Authority Age"     value={authorityAge} valueClass={checklist.find(i => i.key === 'authority_age')?.pass === false ? 'text-red-500' : 'text-foreground'} />
              <DetailRow label="Safety Rating"     value={safetyRating} valueClass={safetyRating === 'Unsatisfactory' ? 'text-red-500' : safetyRating === 'Conditional' ? 'text-amber-500' : 'text-emerald-500'} />
            </div>
            <div>
              <DetailRow label="Allowed to Operate" value={allowedToOperate}
                valueClass={allowedToOperate === 'Allowed' ? 'text-emerald-500 font-semibold' : allowedToOperate === 'Not allowed' ? 'text-red-500 font-semibold' : 'text-muted-foreground'} />
              <DetailRow label="Insurance on File"  value={insuranceStatus} valueClass={insuranceStatus === 'On file' ? 'text-emerald-500' : 'text-red-500'} />
              <DetailRow label="Min. Insurance ($750K)" value={insMin?.value} valueClass={insMin?.pass === false ? 'text-red-500' : 'text-emerald-500'} />
              <DetailRow label="Out of Service"    value={oosStatus} valueClass={oosStatus === 'Out of service' ? 'text-red-500 font-semibold' : 'text-emerald-500'} />
              <DetailRow label="Drivers"           value={report?.fmcsa_driver_count != null ? String(report.fmcsa_driver_count) : null} />
              <DetailRow label="Power Units"       value={report?.fmcsa_power_unit_count != null ? String(report.fmcsa_power_unit_count) : null} />
              <DetailRow label="Active Fleet"      value={fleetItem?.value}
                valueClass={fleetItem?.pass === true ? 'text-emerald-500' : fleetItem?.pass === false ? 'text-red-500' : 'text-muted-foreground'} />
              <DetailRow label="Physical Address (FMCSA)" value={report?.fmcsa_physical_address} />
              {carrier?.dot_number && (
                <div className="pt-1.5">
                  <a
                    href={`https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=USDOT&query_string=${carrier.dot_number}`}
                    target="_blank" rel="noreferrer"
                    className="text-xs text-blue-500 hover:text-blue-400 transition-colors"
                  >
                    View on FMCSA SAFER ↗
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
        {/* FMCSA flag pills */}
        {report?.has_fmcsa_check && checklist.filter(i => i.blocker && i.pass === false).length > 0 && (
          <div className="mt-3 pt-3 border-t border-border space-y-1">
            {checklist.filter(i => i.blocker && i.pass === false).map(i => (
              <Flag2 key={i.key} text={i.description} color="red" />
            ))}
          </div>
        )}
      </SectionCard>

      {/* ── Phone ── */}
      <SectionCard
        icon={Phone}
        title="Phone Verification"
        badge={
          !phone.skipped && phone.valid != null
            ? <StatusBadge status={phone.risk_level || (phone.valid ? 'low' : 'high')} />
            : null
        }
        timestamp={report?.contact_validated_at}
      >
        {phone.skipped ? (
          <p className="text-sm text-muted-foreground py-1">
            {phone.error || 'Not yet validated'} — click <strong>Refresh Contacts</strong>.
          </p>
        ) : phone.error ? (
          <p className="text-sm text-red-500 py-1">{phone.error}</p>
        ) : (
          <div className="grid grid-cols-2 gap-x-8">
            <div>
              <DetailRow label="Number (raw)" value={carrier?.phone} />
              <DetailRow label="Formatted"    value={phone.formatted} valueClass="font-mono" />
              <DetailRow label="Valid"        value={phone.valid === true ? '✓ Yes' : phone.valid === false ? '✗ No' : null}
                valueClass={phone.valid ? 'text-emerald-500' : 'text-red-500'} />
              <DetailRow label="Status"       value={phone.line_status} valueClass={phone.line_status === 'active' ? 'text-emerald-500' : 'text-amber-500'} />
              <DetailRow label="Country"      value={phone.country} />
            </div>
            <div>
              <DetailRow label="Line Type"    value={phone.line_type} />
              <DetailRow label="Carrier"      value={phone.carrier} />
              <DetailRow label="VoIP"         value={phone.is_voip == null ? null : phone.is_voip ? 'Yes' : 'No'}
                valueClass={phone.is_voip ? 'text-amber-500' : 'text-emerald-500'} />
              <DetailRow label="Risk Level"   value={phone.risk_level} valueClass={riskColor(phone.risk_level)} />
            </div>
          </div>
        )}
        {!phone.skipped && !phone.error && (phone.is_abuse || phone.is_disposable) && (
          <div className="mt-2 space-y-1">
            {phone.is_abuse      && <Flag2 text="Abuse detected on this number"  color="red"   />}
            {phone.is_disposable && <Flag2 text="Disposable / temporary number"  color="amber" />}
          </div>
        )}
      </SectionCard>

      {/* ── Email ── */}
      <SectionCard
        icon={Mail}
        title="Email Verification"
        badge={
          !email.skipped && email.deliverable != null
            ? <StatusBadge status={email.deliverable} />
            : null
        }
        timestamp={report?.contact_validated_at}
      >
        {email.skipped ? (
          <p className="text-sm text-muted-foreground py-1">
            {email.error || 'Not yet validated'} — click <strong>Refresh Contacts</strong>.
          </p>
        ) : email.error ? (
          <p className="text-sm text-red-500 py-1">{email.error}</p>
        ) : (
          <div className="grid grid-cols-2 gap-x-8">
            <div>
              <DetailRow label="Address"       value={carrier?.email} valueClass="font-mono text-[11px]" />
              <DetailRow label="Deliverable"   value={email.deliverable ? email.deliverable.charAt(0).toUpperCase() + email.deliverable.slice(1) : null}
                valueClass={email.deliverable === 'deliverable' ? 'text-emerald-500' : email.deliverable === 'undeliverable' ? 'text-red-500' : 'text-amber-500'} />
              <DetailRow label="Format valid"  value={email.is_format_valid === true ? '✓ Yes' : '✗ No'}
                valueClass={email.is_format_valid ? 'text-emerald-500' : 'text-red-500'} />
              <DetailRow label="SMTP valid"    value={email.is_smtp_valid  === true ? '✓ Yes' : '✗ No'}
                valueClass={email.is_smtp_valid  ? 'text-emerald-500' : 'text-red-500'} />
              <DetailRow label="MX record"     value={email.is_mx_valid    === true ? '✓ Found' : '✗ Missing'}
                valueClass={email.is_mx_valid    ? 'text-emerald-500' : 'text-red-500'} />
              <DetailRow label="Quality score" value={email.quality_score != null ? `${(email.quality_score * 100).toFixed(0)}%` : null}
                valueClass={email.quality_score >= 0.8 ? 'text-emerald-500' : email.quality_score >= 0.5 ? 'text-amber-500' : 'text-red-500'} />
            </div>
            <div>
              <DetailRow label="Sender name"   value={email.sender_name} />
              <DetailRow label="Organisation"  value={email.org_name} />
              <DetailRow label="Org type"      value={email.org_type} />
              <DetailRow label="Provider"      value={email.provider} />
              <DetailRow label="Domain age"    value={email.domain_age_days != null ? `${Math.floor(email.domain_age_days / 365)} years` : null}
                valueClass={email.domain_age_days < 365 ? 'text-amber-500' : 'text-foreground'} />
              <DetailRow label="Domain registered" value={fmtDateShort(email.domain_registered)} />
              <DetailRow label="Address risk"  value={email.address_risk} valueClass={riskColor(email.address_risk)} />
              <DetailRow label="Domain risk"   value={email.domain_risk}  valueClass={riskColor(email.domain_risk)}  />
              <DetailRow label="Free email"    value={email.is_free === true ? 'Yes' : email.is_free === false ? 'No' : null}
                valueClass={email.is_free ? 'text-amber-500' : 'text-emerald-500'} />
              <DetailRow label="Role address"  value={email.is_role ? 'Yes (info@, postmaster@…)' : null}
                valueClass="text-amber-500" />
            </div>
          </div>
        )}
        {/* Breach info */}
        {!email.skipped && !email.error && email.breach_count > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                {email.breach_count} data breach{email.breach_count !== 1 ? 'es' : ''} on record
                {email.breach_last_date && ` · last breach ${email.breach_last_date}`}
              </span>
            </div>
            {email.breached_domains?.length > 0 && (
              <p className="text-[10px] text-muted-foreground pl-5">{email.breached_domains.join(' · ')}</p>
            )}
          </div>
        )}
        {!email.skipped && !email.error && (email.is_disposable || email.is_risky_tld) && (
          <div className="mt-2 space-y-1">
            {email.is_disposable && <Flag2 text="Disposable / temporary email address" color="red"   />}
            {email.is_risky_tld  && <Flag2 text="Risky TLD (e.g. .xyz, .tk)"           color="amber" />}
          </div>
        )}
      </SectionCard>

      {/* ── Insurance & Equipment ── */}
      <div className="grid grid-cols-2 gap-3">
        <SectionCard icon={ShieldCheck} title="Insurance & Coverage">
          <DetailRow label="Coverage limit"       value={carrier?.insurance_limit ? `$${carrier.insurance_limit.toLocaleString()}` : 'Not recorded'}
            valueClass={carrier?.insurance_limit >= 750000 ? 'text-emerald-500' : 'text-red-500'} />
          <DetailRow label="FMCSA minimum"        value="$750,000" />
          <DetailRow label="Meets minimum"
            value={carrier?.insurance_limit >= 750000 ? '✓ Yes' : '✗ No'}
            valueClass={carrier?.insurance_limit >= 750000 ? 'text-emerald-500' : 'text-red-500'} />
          <DetailRow label="Insurance on file"    value={insuranceStatus}
            valueClass={insuranceStatus === 'On file' ? 'text-emerald-500' : 'text-red-500'} />
          <DetailRow label="Expiry"               value={fmtDateShort(carrier?.insurance_expiry)} />
        </SectionCard>

        <SectionCard icon={Truck} title="Equipment & Service Area">
          {carrier?.equipment_types?.length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Equipment Types</p>
              <div className="flex flex-wrap gap-1.5">
                {carrier.equipment_types.map(t => (
                  <span key={t} className="px-2 py-0.5 rounded bg-muted text-xs font-medium text-foreground capitalize">{t}</span>
                ))}
              </div>
            </div>
          )}
          {carrier?.regions_served?.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Regions Served</p>
              <p className="text-xs text-foreground">{carrier.regions_served.join(', ')}</p>
            </div>
          )}
          <DetailRow label="Soft straps supported" value={carrier?.soft_straps_supported ? 'Yes' : 'No'} />
        </SectionCard>
      </div>

      {/* ── Identifiers ── */}
      <SectionCard icon={Building2} title="System Identifiers">
        <div className="grid grid-cols-2 gap-x-8">
          <div>
            <DetailRow label="Internal ID"          value={carrier?.id ? <span className="font-mono text-[10px]">{carrier.id}</span> : null} />
            <DetailRow label="Central Dispatch ID"  value={carrier?.customer_id ? <span className="font-mono text-[10px]">{carrier.customer_id}</span> : null} />
            <DetailRow label="Manheim ID"           value={carrier?.manheim_customer_id ? <span className="font-mono text-[10px]">{carrier.manheim_customer_id}</span> : null} />
          </div>
          <div>
            <DetailRow label="Contact name"   value={carrier?.contact_name} />
            <DetailRow label="Member since"   value={fmtDateShort(carrier?.created_at)} />
            <DetailRow label="Profile status" value={carrier?.status?.replace(/_/g, ' ')} />
            <DetailRow label="Total deliveries" value={carrier?.total_deliveries} />
          </div>
        </div>
        {carrier?.authority_numbers?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Authority Numbers</p>
            <div className="space-y-0">
              {carrier.authority_numbers.map(a => (
                <div key={a.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{a.type?.replace(/_/g, ' ')}</p>
                    <p className="font-mono text-xs text-foreground">{a.number}</p>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${a.verified ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                    {a.verified ? 'Verified' : 'Unverified'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      {/* ── BASICS ── */}
      {basics.length > 0 && (
        <SectionCard icon={BarChart3} title="FMCSA BASICS Scores" timestamp={report?.fmcsa_checked_at}>
          <p className="text-[10px] text-muted-foreground mb-3">Percentile rank among all carriers — lower is better. ≥ 65% triggers FMCSA alert.</p>
          <div className="space-y-2.5">
            {basics.map(b => (
              <div key={b.type} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-44 shrink-0">{b.label}</span>
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${b.alert ? 'bg-red-500' : (b.percentile ?? 0) >= 65 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                    style={{ width: `${Math.min(100, b.percentile ?? 0)}%` }}
                  />
                </div>
                <span className={`text-xs tabular-nums font-semibold w-10 text-right ${b.alert ? 'text-red-500' : (b.percentile ?? 0) >= 65 ? 'text-amber-500' : 'text-emerald-500'}`}>
                  {b.percentile != null ? `${b.percentile}%` : '—'}
                </span>
                {b.alert && <span className="text-[10px] text-red-500 font-semibold shrink-0">ALERT</span>}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ── Checklist ── */}
      <SectionCard icon={ClipboardCheck} title="Vetting Checklist">
        {blockers.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Compliance Blockers</p>
            <div className="rounded-lg overflow-hidden border border-border">
              {blockers.map((item, i) => <ChecklistRow key={item.key} item={item} last={i === blockers.length - 1} />)}
            </div>
          </div>
        )}
        {nonBlockers.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Additional Checks</p>
            <div className="rounded-lg overflow-hidden border border-border">
              {nonBlockers.map((item, i) => <ChecklistRow key={item.key} item={item} last={i === nonBlockers.length - 1} />)}
            </div>
          </div>
        )}
      </SectionCard>

      {/* ── Decision ── */}
      <div className="bg-card border border-border rounded-xl p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold text-sm text-foreground">Vetting Decision</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: 'approved', label: 'Approve',  Icon: ThumbsUp,   active: 'bg-emerald-500 text-white', inactive: 'bg-muted text-muted-foreground hover:bg-emerald-500/10 hover:text-emerald-600' },
            { value: 'flagged',  label: 'Flag',      Icon: Flag,       active: 'bg-amber-500 text-white',  inactive: 'bg-muted text-muted-foreground hover:bg-amber-500/10  hover:text-amber-600'  },
            { value: 'rejected', label: 'Reject',    Icon: ThumbsDown, active: 'bg-red-500 text-white',    inactive: 'bg-muted text-muted-foreground hover:bg-red-500/10    hover:text-red-600'    },
          ].map(({ value, label, Icon, active, inactive }) => (
            <button key={value}
              onClick={() => setDecision(decision === value ? '' : value)}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors ${decision === value ? active : inactive}`}>
              <Icon className="h-4 w-4" />{label}
            </button>
          ))}
        </div>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          placeholder="Reason for this decision, follow-up actions, etc."
          className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <Button onClick={handleDecision} disabled={!decision || submitting}
          className="w-full bg-accent text-white hover:bg-accent/90 h-9">
          {submitting
            ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving…</>
            : `Save Decision${decision ? ` — ${decision.charAt(0).toUpperCase() + decision.slice(1)}` : ''}`
          }
        </Button>
        {report?.vetting_notes && (
          <div className="border-t border-border pt-3">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Previous Notes</p>
            <p className="text-xs text-foreground">{report.vetting_notes}</p>
          </div>
        )}
      </div>

    </div>
  );
}


// ── ChecklistRow ───────────────────────────────────────────────────────────────

function ChecklistRow({ item, last }) {
  const [expanded, setExpanded] = useState(false);
  const rowBg = item.pass === false && item.blocker ? 'bg-red-500/5' : item.pass === false ? 'bg-amber-500/5' : '';
  return (
    <div className={`${rowBg}${!last ? ' border-b border-border' : ''}`}>
      <button onClick={() => item.description && setExpanded(v => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left">
        <PassIcon pass={item.pass} />
        <span className="flex-1 text-sm text-foreground">{item.label}</span>
        {item.blocker && item.pass === false && (
          <span className="text-[10px] text-red-500 font-bold uppercase tracking-wide">BLOCKER</span>
        )}
        {item.value != null && (
          <span className={`text-xs font-medium shrink-0 ${item.pass === true ? 'text-emerald-500' : item.pass === false ? 'text-red-500' : 'text-muted-foreground'}`}>
            {item.value}
          </span>
        )}
        {item.weight > 0 && (
          <span className="text-[10px] text-muted-foreground tabular-nums ml-2 hidden sm:block">
            {item.pass === true ? `+${item.weight}` : '+0'} pts
          </span>
        )}
      </button>
      {expanded && item.description && (
        <p className="px-10 pb-2.5 text-xs text-muted-foreground">{item.description}</p>
      )}
    </div>
  );
}
